import React, { useState } from "react"
import moment from "moment"

import Window from "../../../../../components/structure/window/window"
import Button from "../../../../../components/ui/button/button"
import Input from "../../../../../components/ui/inputs/input"
import Textarea from "../../../../../components/ui/inputs/textarea"
import Datepicker from "../../../../../components/ui/datepicker/datepicker"

import { CONFIRMATION_STATUS } from "./helpers"

const initialFromEntry = (entry) => {
	if (!entry) {
		return {
			status: CONFIRMATION_STATUS.CONFIRMED,
			reference: "",
			notes: "",
			receivedDate: moment().valueOf(),
			confirmedBy: "",
		}
	}
	return {
		status: entry.status || CONFIRMATION_STATUS.CONFIRMED,
		reference: entry.reference || "",
		notes: entry.notes || "",
		receivedDate: entry.received_date?.seconds
			? entry.received_date.seconds * 1000
			: moment().valueOf(),
		confirmedBy: entry.confirmed_by || "",
	}
}

/**
 * Modal for adding or editing a confirmation entry.
 *  - ADD  → blank form, status defaults to CONFIRMED
 *  - EDIT → form pre-filled from the entry passed in
 *
 * Deletion uses the global ALERT modal (ModalContext) so users get the same
 * warning-icon confirmation affordance as the rest of the app — see openDelete
 * in confirmations.js.
 */
export default function ConfirmationModal({ mode, item, entry, onSave, onClose }) {
	const [form, setForm] = useState(initialFromEntry(entry))
	const [saving, setSaving] = useState(false)
	const [errors, setErrors] = useState({})

	const itemTypeLabel =
		item?.kind === "HOTEL" ? "hotel" :
		item?.kind === "ROUND" ? "round" :
		item?.kind === "TRANSFER" ? "transfer" :
		item?.kind === "FLIGHT" ? "flight" : "item"

	const validate = () => {
		const next = {}
		if (!form.status) next.status = "Pick a status"
		setErrors(next)
		return Object.keys(next).length === 0
	}

	const handleSave = async () => {
		if (!validate()) return
		setSaving(true)
		try {
			await onSave({
				status: form.status,
				reference: form.reference.trim(),
				notes: form.notes,
				receivedDate: form.receivedDate,
				confirmedBy: form.confirmedBy.trim(),
			})
		} finally {
			setSaving(false)
		}
	}

	const title = mode === "EDIT" ? `Edit ${itemTypeLabel} confirmation` : `Add ${itemTypeLabel} confirmation`

	return (
		<Window title={title} close={onClose}>
			<div className="confirmations-modal-grid">
				<div className="confirmations-modal-row">
					<label>Status</label>
					<div className="confirmations-status-picker">
						<button
							type="button"
							className={form.status === CONFIRMATION_STATUS.CONFIRMED ? "is-active-confirmed" : ""}
							onClick={() => setForm((f) => ({ ...f, status: CONFIRMATION_STATUS.CONFIRMED }))}>
							Confirmed
						</button>
						<button
							type="button"
							className={form.status === CONFIRMATION_STATUS.NOT_CONFIRMED ? "is-active-not-confirmed" : ""}
							onClick={() => setForm((f) => ({ ...f, status: CONFIRMATION_STATUS.NOT_CONFIRMED }))}>
							Not confirmed (action needed)
						</button>
					</div>
					{errors.status && <small style={{ color: "#d3423d" }}>{errors.status}</small>}
				</div>

				<div className="confirmations-modal-row">
					<label>Booking / confirmation reference</label>
					<Input
						placeholder="e.g. ABC123, PNR, hotel res #"
						value={form.reference}
						onChange={(v) => setForm((f) => ({ ...f, reference: v }))}
						error={errors.reference}
					/>
				</div>

				<div className="confirmations-modal-row">
					<label>Date confirmation received</label>
					<Datepicker
						placeholder="Select date"
						value={form.receivedDate || null}
						onSelect={(millis) => setForm((f) => ({ ...f, receivedDate: millis }))}
					/>
				</div>

				<div className="confirmations-modal-row">
					<label>Confirmed by (supplier contact)</label>
					<Input
						placeholder="e.g. Sarah at The Belfry"
						value={form.confirmedBy}
						onChange={(v) => setForm((f) => ({ ...f, confirmedBy: v }))}
					/>
				</div>

				<div className="confirmations-modal-row">
					<label>Notes / supplier reply</label>
					<Textarea
						placeholder="Paste the supplier's email reply here, or add any notes about the confirmation…"
						value={form.notes}
						rows={8}
						onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
					/>
				</div>

				<div className="confirmations-modal-actions">
					<Button label="Cancel" onClick={onClose} />
					<Button
						label="Save"
						loading={saving}
						loadingText="Saving…"
						onClick={handleSave}
					/>
				</div>
			</div>
		</Window>
	)
}
